import React, { useState, useEffect, forwardRef } from 'react';
import { AttributeSensitivity, getBatchEditValue } from '../helpers';
import { Checkbox, Input, Select, TextareaAutosize } from '../components';
import { useMessage } from '../hooks';

const _AddAttributeSensitivityForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    attribute_sensitivity_id: null,
    attribute_sensitivity_name: '',
    attribute_sensitivity_description: '',
    attribute_sensitivity_level: null,
    is_default: false,
    is_allowed_for_pii: false,
  };

  const initialBatchEditItems = {
    batch_is_allowed_for_pii: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const { hideMessage } = useMessage();

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        attribute_sensitivity_id: data[0].attribute_sensitivity_id,
        attribute_sensitivity_name: data[0].attribute_sensitivity_name || '',
        attribute_sensitivity_description: data[0].attribute_sensitivity_description || '',
        attribute_sensitivity_level: data[0].attribute_sensitivity_level,
        is_default: data[0].is_default || false,
        is_allowed_for_pii: data[0].is_allowed_for_pii || false,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        is_allowed_for_pii: getBatchEditValue(data, 'is_allowed_for_pii'),
      });
      //} else if(mode === 'add') {
    } else {
      resetForm();
    }
  }, [mode, data]);

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          attribute_sensitivity_id: item.attribute_sensitivity_id,
          attribute_sensitivity_name: item.attribute_sensitivity_name,
          attribute_sensitivity_level: item.attribute_sensitivity_level,
          attribute_sensitivity_description: item.attribute_sensitivity_description,
          is_default: item.is_default,
          is_allowed_for_pii: item.is_allowed_for_pii,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = formData;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData(prevFormData => {
      return { ...initialFormState };
    });
  };

  const attributeSensitivityLevels = Object.entries(AttributeSensitivity).map(([key, val]) => ({
    value: key.toString(),
    label: formData.attribute_sensitivity_name || `Level ${key}`,
    tooltip: `Sensitivity class ${key}`,
    className: val.className,
  }));

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="attribute_sensitivity_name"
              label="Name"
              value={formData.attribute_sensitivity_name}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.attribute_sensitivity_name
                  ? { valid: false, message: formErrors.attribute_sensitivity_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <TextareaAutosize
              name="attribute_sensitivity_description"
              value={formData.attribute_sensitivity_description}
              onChange={handleInputChange}
              disabled={loading}
              isOptional={true}
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <Select
              name="attribute_sensitivity_level"
              label="Appearance"
              options={attributeSensitivityLevels}
              value={formData.attribute_sensitivity_level}
              onChange={handleInputChange}
              title="Define the sensitivity level, which sets the visual appearance in the GUI"
              disabled={loading}
              placeholder="Select sensitivity class..."
              validation={
                formErrors.attribute_sensitivity_level
                  ? { valid: false, message: formErrors.attribute_sensitivity_level }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <div className="horizontal">
              <Checkbox
                name="is_default"
                checked={formData.is_default}
                onChange={handleInputChange}
                label="Is Default"
                title="Set this as the default sensitivity class when creating new attributes"
                disabled={loading}
              />
            </div>
          </div>
        )}
        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_is_allowed_for_pii}
                name="batch_is_allowed_for_pii"
                label="Edit Allowed For PII"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <Checkbox
              name="is_allowed_for_pii"
              checked={formData.is_allowed_for_pii}
              onChange={handleInputChange}
              label="Is Allowed For PII"
              title="Allow the PII flag to be set for assets with this sensitivity class"
              disabled={loading || (batchEdit && !batchEditItems.batch_is_allowed_for_pii)}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export const AddAttributeSensitivityForm = forwardRef(_AddAttributeSensitivityForm);
